// Turns the "temples" CMS rows (GET /cms?page=temples) into the page content
// shape the sections render, falling back to templesData for anything the
// admin hasn't filled in. The row/child mapping mirrors the admin config in
// thf-admin-main src/Pages/mainPage/Temples/CMS/templeSections.js — keep the
// two in sync.

const isActive = (row) => row && (row.status === 1 || row.status === true || row.status === undefined);
const text = (value, fallback = "") => (typeof value === "string" && value.trim() ? value : fallback);

const sectionOf = (rows, name) => rows.find((row) => row.section === name && isActive(row));
const childrenOf = (section, kind) => (section?.relatedCMS || []).filter((child) => isActive(child) && child.stand_title === kind);
const childOf = (section, kind) => childrenOf(section, kind)[0];

// A list section the admin manages: use its items when the section exists,
// otherwise the defaults. Icons fall back to the default for the same slot.
const listOf = (section, kind, defaults, map) =>
  section ? childrenOf(section, kind).map((child, index) => map(child, defaults[index] || {})) : defaults;

// Restoration stories: children of the "story" row named "<kind>#<n>" (no
// "#n" means story 1, from before there were several). Mirrors the admin's
// StoryEditor.
const buildStories = (section, defaults) => {
  if (!section) return defaults;
  const byNumber = new Map();
  (section.relatedCMS || []).filter(isActive).forEach((child) => {
    const [kind, number] = String(child.stand_title || "").split("#");
    const n = Number(number) || 1;
    if (!byNumber.has(n)) byNumber.set(n, { stats: [] });
    const bucket = byNumber.get(n);
    if (kind === "stat") bucket.stats.push(child);
    else if (!bucket[kind]) bucket[kind] = child;
  });
  const numbers = [...byNumber.keys()].sort((a, b) => a - b);
  if (numbers.length === 0) return defaults;

  return numbers.map((n, index) => {
    const { intro, photos, story, quote, stats } = byNumber.get(n);
    // The first story falls back to the bundled defaults (and to the old
    // section-level one-liner); later stories only show what admin entered.
    const d = index === 0 ? defaults[0] : {};
    return {
      description: text(intro?.description, index === 0 ? text(section.description, d.description) : ""),
      beforeImage: text(photos?.image, d.beforeImage),
      afterImage: text(photos?.image2, d.afterImage),
      storyLabel: text(story?.sub_title, d.storyLabel),
      storyTitle: text(story?.title, d.storyTitle),
      storyParagraphs: story?.description
        ? story.description.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
        : d.storyParagraphs || [],
      quote: text(quote?.title, d.quote),
      quoteAuthor: text(quote?.sub_title, d.quoteAuthor),
      stats: stats.length
        ? stats.map((child) => ({ value: child.title || "", label: child.sub_title || "" }))
        : d.stats || [],
    };
  });
};

// Donation box: the cms row's columns are its texts; children are the header, the
// once/monthly settings and the amount buttons (title = ₹, sub_title = tag on the button).
// Mirrors TEMPLE_DONATION_BOX in the admin.
// "monthly" / "once" / "both"; the first version stored yes (= both) / no (= monthly).
const parseDonationTypes = (value) => {
  const v = String(value || "").trim().toLowerCase();
  if (v === "once" || v === "both") return v;
  if (v === "yes") return "both";
  return "monthly";
};

const buildDonationBox = (section, d) => {
  const amounts = childrenOf(section, "amount")
    .map((child) => ({ amount: Number(String(child.title || "").replace(/[^0-9]/g, "")), label: text(child.sub_title, "") }))
    .filter((a) => a.amount > 0);
  const preset = Number(String(section?.link || "").replace(/[^0-9]/g, ""));
  const header = childOf(section, "header");
  const settings = childOf(section, "settings");
  const donationTypes = settings ? parseDonationTypes(settings.title) : d.donationTypes;
  const defaultMode = String(settings?.sub_title || "").trim().toLowerCase() === "once" ? "once" : d.defaultMode;
  return {
    // An emptied header field hides that line; a missing header row keeps the defaults.
    tagline: header ? text(header.description, "") : d.tagline,
    title: text(header?.title, d.title),
    subtitle: header ? text(header.sub_title, "") : d.subtitle,
    donationTypes,
    defaultMode,
    contributionLabel: text(section?.title, d.contributionLabel),
    buttonLabel: text(section?.sub_title, d.buttonLabel),
    monthlyLabel: section ? text(section.description, "") : d.monthlyLabel,
    amounts: section && amounts.length ? amounts : d.amounts,
    defaultAmount: preset > 0 ? preset : d.defaultAmount,
  };
};

export const buildTemplesContent = (rows, defaults) => {
  if (!Array.isArray(rows) || rows.length === 0) return defaults;

  const hero = sectionOf(rows, "hero");
  const stats = sectionOf(rows, "stats");
  const why = sectionOf(rows, "why");
  const featured = sectionOf(rows, "featured");
  const how = sectionOf(rows, "how");
  const story = sectionOf(rows, "story");
  const sevak = sectionOf(rows, "sevak");
  const nominate = sectionOf(rows, "nominate");
  const donationBox = sectionOf(rows, "donation_box");
  const faq = sectionOf(rows, "faq");

  const d = defaults;
  const primary = childOf(hero, "primary_button");
  const secondary = childOf(hero, "secondary_button");
  const badge = childOf(hero, "badge");
  const whyQuote = childOf(why, "quote");
  const sevakButton = childOf(sevak, "button");
  const stepsHeading = childOf(nominate, "steps_heading");
  const form = childOf(nominate, "form");
  const submit = childOf(nominate, "button");

  return {
    hero: {
      tagline: text(hero?.section_title, d.hero.tagline),
      titleLine1: text(hero?.title, d.hero.titleLine1),
      titleLine2: text(hero?.sub_title, d.hero.titleLine2),
      description: text(hero?.description, d.hero.description),
      primaryCta: {
        label: text(primary?.title, d.hero.primaryCta.label),
        link: text(primary?.link, d.hero.primaryCta.link),
      },
      secondaryCta: {
        label: text(secondary?.title, d.hero.secondaryCta.label),
        link: text(secondary?.link, d.hero.secondaryCta.link),
      },
      trustPoints: hero
        ? childrenOf(hero, "trust_point").map((child) => child.title).filter(Boolean)
        : d.hero.trustPoints,
      image: text(hero?.image, d.hero.image),
      badge: hero && !badge
        ? null
        : { value: text(badge?.title, d.hero.badge.value), label: text(badge?.sub_title, d.hero.badge.label) },
    },

    stats: listOf(stats, "stat", d.stats, (child, fallback) => ({
      icon: text(child.image, fallback.icon),
      value: child.title || "",
      label: child.sub_title || "",
    })),

    whyTemples: {
      tag: text(why?.section_title, d.whyTemples.tag),
      titleStart: text(why?.title, d.whyTemples.titleStart),
      titleHighlight: text(why?.sub_title, d.whyTemples.titleHighlight),
      description: text(why?.description, d.whyTemples.description),
      cards: listOf(why, "card", d.whyTemples.cards, (child, fallback) => ({
        icon: text(child.image, fallback.icon),
        hindiLabel: child.sub_title || "",
        title: child.title || "",
        description: child.description || "",
      })),
      quote: why ? text(whyQuote?.description) : d.whyTemples.quote,
    },

    featuredTemples: {
      tag: text(featured?.section_title, d.featuredTemples.tag),
      title: text(featured?.title, d.featuredTemples.title),
      description: text(featured?.description, d.featuredTemples.description),
    },

    howItWorks: {
      tag: text(how?.section_title, d.howItWorks.tag),
      title: text(how?.title, d.howItWorks.title),
      description: text(how?.description, d.howItWorks.description),
      steps: listOf(how, "step", d.howItWorks.steps, (child, fallback) => ({
        icon: text(child.image, fallback.icon),
        title: child.title || "",
        description: child.description || "",
      })),
    },

    restorationStory: {
      tag: text(story?.section_title, d.restorationStory.tag),
      title: text(story?.title, d.restorationStory.title),
      stories: buildStories(story, d.restorationStory.stories),
    },

    monthlySevak: {
      tagline: text(sevak?.section_title, d.monthlySevak.tagline),
      titleLine1: text(sevak?.title, d.monthlySevak.titleLine1),
      titleLine2: text(sevak?.sub_title, d.monthlySevak.titleLine2),
      description: text(sevak?.description, d.monthlySevak.description),
      cta: {
        label: text(sevakButton?.title, d.monthlySevak.cta.label),
        link: text(sevakButton?.link, d.monthlySevak.cta.link),
      },
    },

    donationBox: buildDonationBox(donationBox, d.donationBox),

    nominate: {
      tag: text(nominate?.section_title, d.nominate.tag),
      titleLine1: text(nominate?.title, d.nominate.titleLine1),
      titleLine2: text(nominate?.sub_title, d.nominate.titleLine2),
      description: text(nominate?.description, d.nominate.description),
      stepsLabel: text(stepsHeading?.title, d.nominate.stepsLabel),
      steps: nominate
        ? childrenOf(nominate, "step").map((child) => child.title).filter(Boolean)
        : d.nominate.steps,
      formTitle: text(form?.title, d.nominate.formTitle),
      formSubtitle: text(form?.sub_title, d.nominate.formSubtitle),
      privacyNote: text(form?.description, d.nominate.privacyNote),
      submitLabel: text(submit?.title, d.nominate.submitLabel),
    },

    faq: {
      title: text(faq?.title, d.faq.title),
      items: listOf(faq, "faq", d.faq.items, (child) => ({
        question: child.title || "",
        answer: child.description || "",
      })).filter((item) => item.question),
    },
  };
};
